// Import-free so node --test can load it: which izqlt.recent rows are real enough to show.

/** Bumped when stored rows need a one-time clean-up; v2 drops the mock-era rows. */
export const RECENT_SCHEMA_VERSION = 2;

export interface RecentRow {
  unitId: string;
  classId: string;
  date: string;
  index: number;
  /** The teacher who closed the period; rows written before v2 lack it. */
  teacherId?: string;
}

const OBJECT_ID = /^[0-9a-f]{24}$/i;
const NUMERIC_ID = /^[0-9]+$/;
/** Ids the board mints itself (see filmstrip/documents.ts); mock-era ids such as `doc-kiem-tra` are not these. */
const GENERATED_PREFIXES = ['doc-quiz-', 'doc-upload-'];

/** A real IZTeach unit id or a board-generated document id. */
export function isRealUnitId(id: string): boolean {
  if (OBJECT_ID.test(id)) return true;
  return GENERATED_PREFIXES.some((prefix) => id.indexOf(prefix) === 0 && id.length > prefix.length);
}

/** A real QLT class id - the mock era used `cls-6a3` and the like. */
export function isRealClassId(id: string): boolean {
  return OBJECT_ID.test(id) || NUMERIC_ID.test(id);
}

/** Enough to address a board route. A row from an older shape degrades to dropped, never to a throw. */
export function usableRecentRow(row: unknown): row is RecentRow {
  const item = row as RecentRow | null;
  if (!item || typeof item !== 'object') return false;
  return typeof item.unitId === 'string' && typeof item.classId === 'string' && typeof item.date === 'string' && typeof item.index === 'number';
}

export interface RecentFilter {
  teacherId?: string | number | null;
  /** The teacher's current classes; empty or omitted means not known yet, so nothing is dropped for it. */
  classIds?: string[];
}

/** A row worth showing: real ids, this teacher's (when stamped), one of their classes (when known). */
export function keepRecentRow(row: RecentRow, filter: RecentFilter = {}): boolean {
  if (!isRealUnitId(row.unitId) || !isRealClassId(row.classId)) return false;
  const teacher = filter.teacherId === null || filter.teacherId === undefined ? '' : String(filter.teacherId);
  if (teacher && row.teacherId && String(row.teacherId) !== teacher) return false;
  const classIds = filter.classIds || [];
  return classIds.length === 0 || classIds.indexOf(row.classId) >= 0;
}

/**
 * The stored rows as the current schema keeps them. Returns `changed` so the caller rewrites the
 * key once; a v2 store is only re-filtered, never rewritten on read.
 */
export function migrateRecentRows<T extends RecentRow>(stored: unknown, version: number | null): { rows: T[]; version: number; changed: boolean } {
  const rows = Array.isArray(stored) ? (stored.filter(usableRecentRow) as T[]) : [];
  if (version !== null && version >= RECENT_SCHEMA_VERSION) return { rows, version, changed: false };
  const kept = rows.filter((row) => keepRecentRow(row));
  return { rows: kept, version: RECENT_SCHEMA_VERSION, changed: true };
}
